import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import {
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowLeftIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';
import { BrandPanel } from './Login';

/* ─── Spinner SVG ─── */
const Spinner: React.FC = () => (
  <svg
    className="animate-spin h-5 w-5 text-white"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

/* ─── Calcul force du mot de passe ─── */
type StrengthLevel = 0 | 1 | 2 | 3 | 4;

interface StrengthInfo {
  level: StrengthLevel;
  label: string;
  color: string;
  barColors: [string, string, string, string];
}

const getStrength = (pwd: string): StrengthInfo => {
  let score = 0;
  if (pwd.length >= 8) score++;
  if (pwd.length >= 12) score++;
  if (/[A-Z]/.test(pwd) && /[a-z]/.test(pwd)) score++;
  if (/[0-9]/.test(pwd)) score++;
  if (/[^A-Za-z0-9]/.test(pwd)) score++;

  if (score <= 1) return {
    level: 1,
    label: 'Très faible',
    color: 'text-red-500',
    barColors: ['bg-red-500', 'bg-gray-200', 'bg-gray-200', 'bg-gray-200'],
  };
  if (score === 2) return {
    level: 2,
    label: 'Faible',
    color: 'text-orange-500',
    barColors: ['bg-orange-400', 'bg-orange-400', 'bg-gray-200', 'bg-gray-200'],
  };
  if (score === 3) return {
    level: 3,
    label: 'Acceptable',
    color: 'text-yellow-500',
    barColors: ['bg-yellow-400', 'bg-yellow-400', 'bg-yellow-400', 'bg-gray-200'],
  };
  return {
    level: 4,
    label: 'Fort',
    color: 'text-primary-600',
    barColors: ['bg-primary-500', 'bg-primary-500', 'bg-primary-500', 'bg-primary-500'],
  };
};

/* ─── Critère de sécurité individuel ─── */
const Criterion: React.FC<{ met: boolean; label: string }> = ({ met, label }) => (
  <li className={`flex items-center gap-1.5 text-xs transition-colors duration-200 ${met ? 'text-primary-600' : 'text-gray-400'}`}>
    <CheckCircleIcon className={`h-3.5 w-3.5 flex-shrink-0 transition-opacity duration-200 ${met ? 'opacity-100' : 'opacity-30'}`} />
    {label}
  </li>
);

/* ──────────────────────────────────────
   Champ mot de passe avec show/hide
────────────────────────────────────── */
interface PasswordFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (val: string) => void;
  show: boolean;
  onToggleShow: () => void;
  placeholder?: string;
  autoComplete?: string;
}

const PasswordField: React.FC<PasswordFieldProps> = ({
  id, label, value, onChange, show, onToggleShow, placeholder = '••••••••', autoComplete,
}) => (
  <div>
    <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1.5">
      {label}
    </label>
    <div className="relative">
      <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
        <LockClosedIcon className="h-5 w-5 text-gray-400" />
      </div>
      <input
        id={id}
        type={show ? 'text' : 'password'}
        required
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        className="block w-full pl-10 pr-11 py-3 border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent focus:bg-white transition-all duration-150 text-sm"
        placeholder={placeholder}
      />
      <button
        type="button"
        onClick={onToggleShow}
        className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
        aria-label={show ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
      >
        {show ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
      </button>
    </div>
  </div>
);

/* ══════════════════════════════════════
   PAGE RESET PASSWORD
══════════════════════════════════════ */
const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [countdown, setCountdown] = useState(3);

  /* Countdown de redirection */
  useEffect(() => {
    if (!success) return;
    if (countdown <= 0) {
      navigate('/login');
      return;
    }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [success, countdown, navigate]);

  const strength = newPassword.length > 0 ? getStrength(newPassword) : null;

  const criteriaList = [
    { met: newPassword.length >= 8, label: 'Au moins 8 caractères' },
    { met: /[A-Z]/.test(newPassword), label: 'Une majuscule' },
    { met: /[0-9]/.test(newPassword), label: 'Un chiffre' },
    { met: /[^A-Za-z0-9]/.test(newPassword), label: 'Un caractère spécial' },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }

    setIsLoading(true);
    try {
      await apiService.resetPassword(token, newPassword, confirmPassword);
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Token invalide ou expiré. Demandez un nouveau lien.');
    } finally {
      setIsLoading(false);
    }
  };

  /* ── Token manquant / invalide ── */
  if (!token) {
    return (
      <div className="min-h-screen flex">
        <BrandPanel subtitle="Réinitialisez votre mot de passe en toute sécurité" />

        <div className="flex-1 flex flex-col items-center justify-center bg-white px-6 py-12 sm:px-12">
          <div className="w-full max-w-md text-center">
            {/* Icône erreur */}
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center">
                  <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center">
                    <ShieldExclamationIcon className="w-8 h-8 text-red-500" />
                  </div>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-3">Lien invalide</h2>
            <p className="text-sm text-gray-500 mb-2 leading-relaxed">
              Ce lien de réinitialisation est absent, invalide ou a expiré.
            </p>
            <p className="text-sm text-gray-500 mb-8 leading-relaxed">
              Les liens de réinitialisation sont valables <span className="font-medium text-gray-700">1 heure</span> après leur émission.
            </p>

            {/* Card info */}
            <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 mb-8 text-left">
              <div className="flex gap-3 items-start">
                <ExclamationCircleIcon className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-700">
                  Demandez un nouveau lien de réinitialisation depuis la page dédiée.
                </p>
              </div>
            </div>

            <Link
              to="/forgot-password"
              className="w-full flex items-center justify-center gap-2 py-3.5 px-4 rounded-xl text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-150 shadow-sm shadow-primary-200"
            >
              Demander un nouveau lien
            </Link>

            <Link
              to="/login"
              className="mt-4 inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-primary-600 transition-colors"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Retour à la connexion
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* ── Panneau gauche branding ── */}
      <BrandPanel subtitle="Choisissez un mot de passe sécurisé pour protéger votre espace" />

      {/* ── Panneau droit ── */}
      <div className="flex-1 flex flex-col items-center justify-center bg-white px-6 py-12 sm:px-12">

        {/* Logo mobile */}
        <div className="lg:hidden flex flex-col items-center mb-10">
          <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center mb-3 shadow-lg overflow-hidden border border-gray-100">
            <img src="/armoirie.jpeg" alt="Armoiries du Burkina Faso" className="w-full h-full object-contain p-1" />
          </div>
          <span className="text-lg font-bold text-gray-900">Suivi Démat</span>
          <span className="text-xs text-primary-600 font-semibold tracking-widest uppercase mt-0.5">MTDPCE</span>
        </div>

        <div className="w-full max-w-md">

          {/* Lien retour */}
          {!success && (
            <Link
              to="/forgot-password"
              className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-primary-600 mb-8 transition-colors group"
            >
              <ArrowLeftIcon className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" />
              Demander un autre lien
            </Link>
          )}

          {/* ── État succès ── */}
          {success ? (
            <div className="animate-[fadeIn_0.3s_ease-out] text-center">
              {/* Icône succès */}
              <div className="flex justify-center mb-6">
                <div className="relative">
                  <div className="w-20 h-20 bg-primary-50 rounded-full flex items-center justify-center">
                    <div className="w-14 h-14 bg-primary-100 rounded-full flex items-center justify-center">
                      <CheckCircleIcon className="w-9 h-9 text-primary-600" />
                    </div>
                  </div>
                  <span className="absolute inset-0 rounded-full border-2 border-primary-300 animate-ping opacity-30" />
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-3">
                Mot de passe mis à jour !
              </h2>
              <p className="text-sm text-gray-500 mb-2 leading-relaxed">
                Votre mot de passe a été réinitialisé avec succès.
              </p>

              {/* Countdown */}
              <div className="my-6 flex flex-col items-center gap-3">
                <div className="relative w-16 h-16">
                  {/* Cercle SVG progress */}
                  <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64" aria-hidden="true">
                    <circle cx="32" cy="32" r="28" fill="none" stroke="#e5e7eb" strokeWidth="4" />
                    <circle
                      cx="32" cy="32" r="28"
                      fill="none"
                      stroke="#16a34a"
                      strokeWidth="4"
                      strokeDasharray={`${2 * Math.PI * 28}`}
                      strokeDashoffset={`${2 * Math.PI * 28 * (1 - countdown / 3)}`}
                      strokeLinecap="round"
                      className="transition-all duration-1000"
                    />
                  </svg>
                  <span className="absolute inset-0 flex items-center justify-center text-xl font-bold text-primary-600">
                    {countdown}
                  </span>
                </div>
                <p className="text-sm text-gray-400">
                  Redirection vers la connexion dans <span className="font-medium text-gray-600">{countdown}s</span>…
                </p>
              </div>

              <Link
                to="/login"
                className="w-full flex items-center justify-center gap-2 py-3.5 px-4 rounded-xl text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-150 shadow-sm shadow-primary-200"
              >
                Se connecter maintenant
              </Link>
            </div>
          ) : (
            /* ── Formulaire ── */
            <>
              <div className="mb-8">
                <div className="w-14 h-14 bg-white border border-gray-100 rounded-2xl flex items-center justify-center mb-6 shadow-sm overflow-hidden">
                  <img src="/armoirie.jpeg" alt="Armoiries du Burkina Faso" className="w-full h-full object-contain p-1" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 leading-tight">
                  Nouveau mot de passe
                </h2>
                <p className="mt-2 text-sm text-gray-500 leading-relaxed">
                  Choisissez un mot de passe sécurisé d'au moins 8 caractères.
                </p>
              </div>

              {/* Erreur */}
              {error && (
                <div
                  className="mb-5 flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3.5 rounded-xl text-sm animate-[fadeIn_0.25s_ease-in-out]"
                  role="alert"
                >
                  <XCircleIcon className="h-5 w-5 flex-shrink-0 text-red-500 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <form className="space-y-5" onSubmit={handleSubmit} noValidate>

                {/* Nouveau mot de passe */}
                <PasswordField
                  id="new-password"
                  label="Nouveau mot de passe"
                  value={newPassword}
                  onChange={setNewPassword}
                  show={showNew}
                  onToggleShow={() => setShowNew(!showNew)}
                  autoComplete="new-password"
                />

                {/* ── Indicateur de force ── */}
                {newPassword.length > 0 && strength && (
                  <div className="space-y-2 animate-[fadeIn_0.2s_ease-out]">
                    {/* Barres */}
                    <div className="flex gap-1.5">
                      {strength.barColors.map((color, i) => (
                        <div
                          key={i}
                          className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${color}`}
                        />
                      ))}
                    </div>
                    {/* Label force */}
                    <div className="flex items-center justify-between">
                      <span className={`text-xs font-semibold ${strength.color}`}>
                        {strength.label}
                      </span>
                    </div>
                    {/* Critères */}
                    <ul className="grid grid-cols-2 gap-x-4 gap-y-1 pt-1">
                      {criteriaList.map((c, i) => (
                        <Criterion key={i} met={c.met} label={c.label} />
                      ))}
                    </ul>
                  </div>
                )}

                {/* Confirmation mot de passe */}
                <div>
                  <PasswordField
                    id="confirm-password"
                    label="Confirmer le mot de passe"
                    value={confirmPassword}
                    onChange={setConfirmPassword}
                    show={showConfirm}
                    onToggleShow={() => setShowConfirm(!showConfirm)}
                    autoComplete="new-password"
                  />
                  {/* Feedback correspondance */}
                  {confirmPassword.length > 0 && (
                    <p
                      className={`mt-1.5 text-xs flex items-center gap-1 transition-colors duration-200 ${
                        confirmPassword === newPassword ? 'text-primary-600' : 'text-red-500'
                      }`}
                    >
                      {confirmPassword === newPassword ? (
                        <>
                          <CheckCircleIcon className="h-3.5 w-3.5" />
                          Les mots de passe correspondent
                        </>
                      ) : (
                        <>
                          <XCircleIcon className="h-3.5 w-3.5" />
                          Les mots de passe ne correspondent pas
                        </>
                      )}
                    </p>
                  )}
                </div>

                {/* Bouton */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex items-center justify-center gap-2 py-3.5 px-4 rounded-xl text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 active:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-150 shadow-sm shadow-primary-200 mt-2"
                >
                  {isLoading ? (
                    <>
                      <Spinner />
                      <span>Enregistrement…</span>
                    </>
                  ) : (
                    <>
                      <LockClosedIcon className="h-4 w-4" />
                      <span>Enregistrer le nouveau mot de passe</span>
                    </>
                  )}
                </button>
              </form>
            </>
          )}
        </div>

        {/* Footer mobile */}
        <p className="lg:hidden mt-10 text-center text-xs text-gray-400">
          © 2026 MTDPCE — Burkina Faso
        </p>
      </div>
    </div>
  );
};

export default ResetPassword;
